import { useState, useCallback, useMemo, useEffect } from 'react';
import type { SignedConsent, CustomerConsentGroup, ConsentTemplate } from '../types';
import { initialSignedConsents } from '../mockData';

// 샘플 서명 이미지 생성 함수 (PNG Canvas 기반)
const generateSampleSignature = (seed: number): string => {
    const canvas = document.createElement('canvas');
    canvas.width = 200;
    canvas.height = 60;
    const ctx = canvas.getContext('2d');
    if (!ctx) return '';

    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, 200, 60);

    ctx.strokeStyle = '#1a1a1a';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.beginPath();

    if (seed === 1) {
        ctx.moveTo(10, 40);
        ctx.quadraticCurveTo(30, 10, 50, 30);
        ctx.quadraticCurveTo(70, 50, 90, 25);
        ctx.quadraticCurveTo(110, 5, 130, 35);
        ctx.quadraticCurveTo(150, 55, 170, 30);
        ctx.lineTo(190, 35);
    } else if (seed === 2) {
        ctx.moveTo(15, 35);
        ctx.quadraticCurveTo(40, 15, 60, 40);
        ctx.quadraticCurveTo(80, 55, 100, 30);
        ctx.quadraticCurveTo(120, 10, 140, 35);
        ctx.quadraticCurveTo(155, 50, 175, 25);
    } else {
        ctx.moveTo(20, 30);
        ctx.quadraticCurveTo(50, 55, 80, 35);
        ctx.quadraticCurveTo(100, 20, 120, 40);
        ctx.quadraticCurveTo(140, 55, 160, 25);
        ctx.quadraticCurveTo(175, 10, 185, 40);
    }
    ctx.stroke();
    return canvas.toDataURL('image/png');
};

// 초기 상태 생성 함수 (서명 포함)
const createInitialSignedConsents = (): SignedConsent[] => {
    // 서명 이미지 동기적으로 생성
    const sig1 = generateSampleSignature(1);
    const sig2 = generateSampleSignature(2);
    const sig3 = generateSampleSignature(3);

    return initialSignedConsents.map((consent) => {
        if (consent.signatureImageUrl) return consent;
        if (consent.customerName === '김철수') {
            return { ...consent, signatureImageUrl: sig1 };
        } else if (consent.customerName === '이영희') {
            return { ...consent, signatureImageUrl: sig2 };
        } else if (consent.customerName === '박민수') {
            return { ...consent, signatureImageUrl: sig3 };
        }
        return consent;
    });
};

export function useSignedConsents(templates: ConsentTemplate[]) {
    // 서명된 동의서 목록 (초기화 시 서명 적용)
    const [signedConsentsList, setSignedConsentsList] = useState<SignedConsent[]>(() => createInitialSignedConsents());

    // 샘플 서명 (새로운 서명 요청 시 사용)
    const sampleSignatures = useMemo(() => ({
        signature1: generateSampleSignature(1),
        signature2: generateSampleSignature(2),
        signature3: generateSampleSignature(3),
    }), []);

    // 모달 상태
    const [isSignedDetailModalOpen, setIsSignedDetailModalOpen] = useState(false);
    const [selectedSignedConsent, setSelectedSignedConsent] = useState<SignedConsent | null>(null);
    const [isCustomerDetailModalOpen, setIsCustomerDetailModalOpen] = useState(false);
    const [selectedCustomerGroup, setSelectedCustomerGroup] = useState<CustomerConsentGroup | null>(null);
    const [selectedConsentForPreview, setSelectedConsentForPreview] = useState<SignedConsent | null>(null);

    // 필터 상태
    const [searchConsentQuery, setSearchConsentQuery] = useState('');
    const [filterTemplateId, setFilterTemplateId] = useState<string>('');
    const [filterDateFrom, setFilterDateFrom] = useState('');
    const [filterDateTo, setFilterDateTo] = useState('');
    const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

    // 검색 필터링
    const filteredSignedConsents = useMemo(() => {
        return signedConsentsList.filter((consent) => {
            if (!searchConsentQuery) return true;
            const query = searchConsentQuery.toLowerCase();
            return (
                consent.customerName.toLowerCase().includes(query) ||
                consent.petName.toLowerCase().includes(query) ||
                consent.templateName.toLowerCase().includes(query)
            );
        });
    }, [signedConsentsList, searchConsentQuery]);

    // 고급 필터링
    const advancedFilteredConsents = useMemo(() => {
        return filteredSignedConsents.filter((consent) => {
            if (filterTemplateId && consent.templateId !== filterTemplateId) {
                return false;
            }
            if (filterDateFrom) {
                const consentDate = new Date(consent.signedAt);
                const fromDate = new Date(filterDateFrom);
                if (consentDate < fromDate) return false;
            }
            if (filterDateTo) {
                const consentDate = new Date(consent.signedAt);
                const toDate = new Date(filterDateTo);
                toDate.setHours(23, 59, 59, 999);
                if (consentDate > toDate) return false;
            }
            return true;
        });
    }, [filteredSignedConsents, filterTemplateId, filterDateFrom, filterDateTo]);

    // 고객별 그룹화
    const customerConsentGroups = useMemo(() => {
        const groupMap = new Map<string, CustomerConsentGroup>();

        advancedFilteredConsents.forEach((consent) => {
            const customerId = consent.customerPhone;

            if (groupMap.has(customerId)) {
                const existing = groupMap.get(customerId)!;
                existing.consents.push(consent);
                existing.totalConsents++;
                const petKey = `${consent.petName}-${consent.petSpecies}`;
                if (!existing.pets.some((p) => `${p.name}-${p.species}` === petKey)) {
                    existing.pets.push({
                        name: consent.petName,
                        species: consent.petSpecies || '미분류',
                        breed: consent.petBreed,
                    });
                }
                if (new Date(consent.signedAt) > new Date(existing.lastSignedAt)) {
                    existing.lastSignedAt = consent.signedAt;
                }
            } else {
                groupMap.set(customerId, {
                    customerId,
                    customerName: consent.customerName,
                    customerPhone: consent.customerPhone,
                    pets: [{
                        name: consent.petName,
                        species: consent.petSpecies || '미분류',
                        breed: consent.petBreed,
                    }],
                    consents: [consent],
                    totalConsents: 1,
                    lastSignedAt: consent.signedAt,
                });
            }
        });

        return Array.from(groupMap.values()).sort(
            (a, b) => new Date(b.lastSignedAt).getTime() - new Date(a.lastSignedAt).getTime()
        );
    }, [advancedFilteredConsents]);

    // 통계 계산
    const consentStats = useMemo(() => {
        const now = new Date();
        const thisMonth = now.getMonth();
        const thisYear = now.getFullYear();

        const thisMonthConsents = signedConsentsList.filter((c) => {
            const date = new Date(c.signedAt);
            return date.getMonth() === thisMonth && date.getFullYear() === thisYear;
        });

        const byTemplate = templates.map((t) => ({
            name: t.name,
            count: signedConsentsList.filter((c) => c.templateId === String(t.id)).length,
        }));

        return {
            total: signedConsentsList.length,
            thisMonth: thisMonthConsents.length,
            byTemplate,
        };
    }, [signedConsentsList, templates]);

    // 상세 모달 열기
    const openDetailModal = useCallback((consent: SignedConsent) => {
        setSelectedSignedConsent(consent);
        setIsSignedDetailModalOpen(true);
    }, []);

    // 고객 상세 모달 열기
    const openCustomerDetailModal = useCallback((group: CustomerConsentGroup) => {
        setSelectedCustomerGroup(group);
        setSelectedConsentForPreview(group.consents[0] || null);
        setIsCustomerDetailModalOpen(true);
    }, []);

    // 미리보기용 동의서 선택
    const selectConsentForPreview = useCallback((consent: SignedConsent) => {
        setSelectedConsentForPreview(consent);
    }, []);

    // 모달 닫기
    const closeDetailModal = useCallback(() => {
        setIsSignedDetailModalOpen(false);
        setSelectedSignedConsent(null);
    }, []);

    const closeCustomerDetailModal = useCallback(() => {
        setIsCustomerDetailModalOpen(false);
        setSelectedCustomerGroup(null);
        setSelectedConsentForPreview(null);
    }, []);

    // 필터 초기화
    const resetFilters = useCallback(() => {
        setSearchConsentQuery('');
        setFilterTemplateId('');
        setFilterDateFrom('');
        setFilterDateTo('');
    }, []);

    // 새 서명된 동의서 추가
    const addSignedConsent = useCallback((consent: SignedConsent) => {
        setSignedConsentsList((prev) => [consent, ...prev]);
    }, []);

    return {
        // State
        signedConsentsList,
        filteredSignedConsents: advancedFilteredConsents,
        customerConsentGroups,
        consentStats,
        sampleSignatures,

        // Modal state
        isSignedDetailModalOpen,
        selectedSignedConsent,
        isCustomerDetailModalOpen,
        selectedCustomerGroup,
        selectedConsentForPreview,

        // Filter state
        searchConsentQuery,
        filterTemplateId,
        filterDateFrom,
        filterDateTo,
        showAdvancedFilters,

        // Filter actions
        setSearchConsentQuery,
        setFilterTemplateId,
        setFilterDateFrom,
        setFilterDateTo,
        setShowAdvancedFilters,
        resetFilters,

        // Modal actions
        openDetailModal,
        closeDetailModal,
        openCustomerDetailModal,
        closeCustomerDetailModal,
        selectConsentForPreview,

        // Setters (for direct JSX control)
        setIsSignedDetailModalOpen,
        setIsCustomerDetailModalOpen,

        // Data actions
        addSignedConsent,
    };
}
